import { useState, useMemo } from "react";
import { Filter, X, ArrowUpDown, Star, Repeat2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

export interface FeedFiltersState {
  scoreRange: "all" | "85+" | "70-84" | "50-69" | "<50";
  promptTypes: string[];
  tags: string[];
  engagementTypes: string[];
  sortOrder: "desc" | "asc";
}

interface FeedFiltersProps {
  filters: FeedFiltersState;
  onFiltersChange: (filters: FeedFiltersState) => void;
  availableTags: string[];
}

const PROMPT_TYPES = [
  "Planning",
  "Research",
  "Analysis",
  "Strategy",
  "Drafting",
  "Creative",
  "Brainstorming",
  "Summarizing",
];

const SCORE_RANGES = [
  { value: "all", label: "All" },
  { value: "85+", label: "85+" },
  { value: "70-84", label: "70-84" },
  { value: "50-69", label: "50-69" },
  { value: "<50", label: "Below 50" },
] as const;

const ENGAGEMENT_OPTIONS = [
  { value: "favorited", label: "Favorites", icon: Star },
  { value: "remixed", label: "Remixes", icon: Repeat2 },
] as const;

export function FeedFilters({
  filters,
  onFiltersChange,
  availableTags,
}: FeedFiltersProps) {
  const [open, setOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState("");
  const isMobile = useIsMobile();

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.scoreRange !== "all") count++;
    if (filters.promptTypes.length > 0) count++;
    if (filters.tags.length > 0) count++;
    if (filters.engagementTypes.length > 0) count++;
    return count;
  }, [filters]);

  // Contextual sort label based on active filters
  const getSortLabel = () => {
    if (filters.scoreRange !== "all") {
      return filters.sortOrder === "desc" ? "Highest Score" : "Lowest Score";
    }
    if (filters.engagementTypes.includes("favorited")) {
      return filters.sortOrder === "desc" ? "Most Favorites" : "Least Favorites";
    }
    if (filters.engagementTypes.includes("remixed")) {
      return filters.sortOrder === "desc" ? "Most Remixes" : "Least Remixes";
    }
    return filters.sortOrder === "desc" ? "Newest" : "Oldest";
  };

  const handleScoreChange = (value: string) => {
    onFiltersChange({
      ...filters,
      scoreRange: value as FeedFiltersState["scoreRange"],
    });
  };

  const handlePromptTypeToggle = (type: string) => {
    const newTypes = filters.promptTypes.includes(type)
      ? filters.promptTypes.filter((t) => t !== type)
      : [...filters.promptTypes, type];
    onFiltersChange({ ...filters, promptTypes: newTypes });
  };

  const handleTagToggle = (tag: string) => {
    const newTags = filters.tags.includes(tag)
      ? filters.tags.filter((t) => t !== tag)
      : [...filters.tags, tag];
    onFiltersChange({ ...filters, tags: newTags });
    setTagSearch("");
  };

  const handleEngagementToggle = (value: string) => {
    const newTypes = filters.engagementTypes.includes(value)
      ? filters.engagementTypes.filter((t) => t !== value)
      : [...filters.engagementTypes, value];
    onFiltersChange({ ...filters, engagementTypes: newTypes });
  };

  const handleSortToggle = () => {
    onFiltersChange({
      ...filters,
      sortOrder: filters.sortOrder === "desc" ? "asc" : "desc",
    });
  };

  const handleClearAll = () => {
    onFiltersChange({
      scoreRange: "all",
      promptTypes: [],
      tags: [],
      engagementTypes: [],
      sortOrder: "desc",
    });
  };

  const filteredTags = useMemo(() => {
    if (!tagSearch) return availableTags.slice(0, 10);
    return availableTags
      .filter((tag) => tag.toLowerCase().includes(tagSearch.toLowerCase()))
      .slice(0, 10);
  }, [availableTags, tagSearch]);

  // Shared filter content
  const filterContent = (
    <div className="space-y-4">
      {/* Score Range */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Score Range
        </Label>
        <RadioGroup
          value={filters.scoreRange}
          onValueChange={handleScoreChange}
          className="flex flex-wrap gap-2"
        >
          {SCORE_RANGES.map((range) => (
            <div key={range.value} className="flex items-center">
              <RadioGroupItem
                value={range.value}
                id={`score-${range.value}`}
                className="peer sr-only"
              />
              <Label
                htmlFor={`score-${range.value}`}
                className={cn(
                  "px-3 py-1.5 rounded-md text-sm cursor-pointer transition-colors border",
                  filters.scoreRange === range.value
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background hover:bg-muted border-border"
                )}
              >
                {range.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Prompt Type */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Prompt Type
        </Label>
        <div className="flex flex-wrap gap-1.5">
          {PROMPT_TYPES.map((type) => (
            <Button
              key={type}
              variant="outline"
              size="sm"
              onClick={() => handlePromptTypeToggle(type)}
              className={cn(
                "h-7 text-xs",
                filters.promptTypes.includes(type) &&
                  "bg-primary text-primary-foreground hover:bg-primary/90"
              )}
            >
              {type}
            </Button>
          ))}
        </div>
      </div>

      {/* Tags */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Tags
        </Label>
        {filters.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {filters.tags.map((tag) => (
              <Badge
                key={tag}
                variant="secondary"
                className="gap-1 pr-1"
              >
                {tag}
                <button
                  onClick={() => handleTagToggle(tag)}
                  className="ml-1 hover:bg-muted-foreground/20 rounded-sm"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
        <Command className="border rounded-md">
          <CommandInput
            placeholder="Search tags..."
            value={tagSearch}
            onValueChange={setTagSearch}
            className="h-9"
          />
          <CommandList className="max-h-32">
            <CommandEmpty className="py-2 text-center text-sm text-muted-foreground">
              No tags found
            </CommandEmpty>
            <CommandGroup>
              {filteredTags
                .filter((tag) => !filters.tags.includes(tag))
                .map((tag) => (
                  <CommandItem
                    key={tag}
                    value={tag}
                    onSelect={() => handleTagToggle(tag)}
                    className="cursor-pointer"
                  >
                    {tag}
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </div>

      {/* Engagement */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Engagement
        </Label>
        <div className="flex flex-wrap gap-1.5">
          {ENGAGEMENT_OPTIONS.map((option) => (
            <Button
              key={option.value}
              variant="outline"
              size="sm"
              onClick={() => handleEngagementToggle(option.value)}
              className={cn(
                "h-7 text-xs gap-1.5",
                filters.engagementTypes.includes(option.value) &&
                  "bg-primary text-primary-foreground hover:bg-primary/90"
              )}
            >
              <option.icon className="h-3.5 w-3.5" />
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Clear All */}
      {activeFilterCount > 0 && (
        <div className="pt-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="w-full text-muted-foreground"
          >
            Clear All Filters
          </Button>
        </div>
      )}
    </div>
  );

  const filterTrigger = (
    <Button variant="outline" size="icon" className="h-8 w-8 relative">
      <Filter className="h-4 w-4" />
      {activeFilterCount > 0 && (
        <Badge
          variant="secondary"
          className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]"
        >
          {activeFilterCount}
        </Badge>
      )}
    </Button>
  );

  return (
    <div className="flex items-center gap-2">
      {isMobile ? (
        // Full-screen sheet on mobile
        <Sheet open={open} onOpenChange={setOpen}>
          <Button 
            variant="outline" 
            size="icon" 
            className="h-8 w-8 relative"
            onClick={() => setOpen(true)}
          >
            <Filter className="h-4 w-4" />
            {activeFilterCount > 0 && (
              <Badge
                variant="secondary"
                className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]"
              >
                {activeFilterCount}
              </Badge>
            )}
          </Button>
          <SheetContent side="bottom" className="h-full rounded-none p-0" hideCloseButton>
            <div className="flex flex-col h-full">
              <SheetHeader className="flex-shrink-0 px-4 py-3 border-b">
                <div className="flex items-center justify-between">
                  <SheetTitle>Filters</SheetTitle>
                  <SheetClose asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <X className="h-5 w-5" />
                    </Button>
                  </SheetClose>
                </div>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto p-4">
                {filterContent}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        // Popover on desktop
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            {filterTrigger}
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            <div className="p-4">
              {filterContent}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Sort Order Toggle */}
      <Button
        variant="ghost"
        size="icon"
        onClick={handleSortToggle}
        className="h-8 w-8 text-muted-foreground"
        title={getSortLabel()}
      >
        <ArrowUpDown className="h-4 w-4" />
      </Button>
    </div>
  );
}
